import { invariantResponse } from '@epic-web/invariant'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	type LoaderFunctionArgs,
	Outlet,
	useLoaderData,
	useNavigate,
	useParams,
} from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import { z } from 'zod'
import Breadcrumbs from '#app/components/breadcrumbs.tsx'
import { type TooltipX } from '#app/components/elevation-chart.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { type MarkerData } from '#app/components/leaflet-map.client.tsx'
import TrailCaptureDesktopLayout from '#app/components/trail-capture-desktop-layout.tsx'
import TrailCaptureMobileLayout from '#app/components/trail-capture-mobile-layout.tsx'
import { useStaticCapture } from '#app/hooks/use-static-capture.ts'
import { toExplorePath } from '#app/routes/explore_+/_to-path.ts'
import { prisma } from '#app/utils/db.server.ts'

import { SlugSchema as CaptureSlugSchema } from '#app/utils/trail-capture-validation.ts'
import {
	CountrySchema,
	RegionSchema,
	SlugSchema as TrailSlugSchema,
	TypeSchema,
} from '#app/utils/trail-validation.ts'

const PathParamsSchema = z.object({
	country: CountrySchema,
	region: RegionSchema,
	type: TypeSchema,
	'trail-slug': TrailSlugSchema,
	'capture-slug': CaptureSlugSchema,
})

export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async () => {
		const trails = await prisma.trail.findMany({
			select: {
				country: true,
				region: true,
				slug: true,
				defaultCapture: {
					select: {
						slug: true,
						defaultFrameId: true,
					},
				},
			},
			where: {
				defaultCaptureId: {
					not: null,
				},
			},
		})
		return trails.map((trail) => {
			return {
				route: toExplorePath(
					trail.country,
					trail.region,
					'trail',
					trail.slug,
					trail.defaultCapture?.slug,
					trail.defaultCapture?.defaultFrameId || '0',
				),
				priority: 0.7,
			}
		})
	}),
}

type LoaderData = {
	trail: {
		id: string
		country: string
		region: string
		slug: string
		name: string | null
		details: string | null
		length: number | null
		elevation_gain: number | null
		latitude: number | null
		longitude: number | null
		updatedAt: string
		captures: Array<{ id: string; slug: string }>
	}
	trailCapture: {
		id: string
		slug: string
		description: string | null
		defaultFrameId: string | null
		owner?: { username: string }
		tags: Array<{ tag: { name: string; slug: string } }>
	}
	captureId: string
	updatedAtDisplay: string
}

export async function loader({ request, params }: LoaderFunctionArgs) {
	const parsed = PathParamsSchema.safeParse(params)
	invariantResponse(
		parsed.success,
		`Invalid path parameters: ${parsed.error}`,
		{ status: 400 },
	)
	const {
		country,
		region,
		'trail-slug': trailSlug,
		'capture-slug': captureSlug,
	} = parsed.data

	const url = new URL(request.url)

	const trail = await prisma.trail.findUnique({
		where: {
			country_region_slug: { country, region, slug: trailSlug },
		},
		select: {
			id: true,
			country: true,
			region: true,
			slug: true,
			name: true,
			details: true,
			length: true,
			elevation_gain: true,
			latitude: true,
			longitude: true,
			updatedAt: true,
			captures: {
				select: { id: true, slug: true },
				orderBy: { slug: 'asc' },
			},
		},
	})

	invariantResponse(trail, `Trail not found for ${url.pathname}`, {
		status: 404,
	})

	const capture = await prisma.trailCapture.findFirst({
		where: { trailId: trail.id, slug: captureSlug },
		select: {
			id: true,
			slug: true,
			defaultFrameId: true,
			description: true,
			owner: { select: { username: true } },
			tags: { select: { tag: { select: { name: true, slug: true } } } },
		},
	})

	invariantResponse(capture, `TrailCapture not found for ${url.pathname}`, {
		status: 404,
	})

	return {
		trail: {
			...trail,
			updatedAt: trail.updatedAt.toISOString(),
		},
		trailCapture: {
			id: capture.id,
			slug: capture.slug,
			description: capture.description,
			defaultFrameId: capture.defaultFrameId || '0',
			owner: capture.owner ? { username: capture.owner.username } : undefined,
			tags: capture.tags,
		},
		captureId: capture.id,
		updatedAtDisplay: trail.updatedAt.toLocaleDateString(),
	} satisfies LoaderData
}

export const shouldRevalidate = ({
	currentParams,
	nextParams,
}: {
	currentParams: Record<string, string>
	nextParams: Record<string, string>
}) => {
	return (
		currentParams.country !== nextParams.country ||
		currentParams.region !== nextParams.region ||
		currentParams.type !== nextParams.type ||
		currentParams['trail-slug'] !== nextParams['trail-slug'] ||
		currentParams['capture-slug'] !== nextParams['capture-slug']
		// ignore "frame-id"
	)
}

export default function TrailCaptureLayout() {
	const { trail, trailCapture, captureId, updatedAtDisplay } =
		useLoaderData<typeof loader>()
	const navigate = useNavigate()
	const params = useParams() // to know current frame-id without reloading data

	const { captureData, error } = useStaticCapture(captureId)

	const currentFrameId = params['frame-id']

	const [activeTab, setActiveTab] = useState<
		'image' | 'chart' | 'map' | 'description' | 'help'
	>('image')
	const [blip, setBlip] = useState<TooltipX | null>(null)
	const [isDesktop, setIsDesktop] = useState(true)

	useEffect(() => {
		if (error) throw error
	}, [error])

	useEffect(() => {
		const checkScreenSize = () => {
			setIsDesktop(window.innerWidth >= 1024) // lg breakpoint
		}

		checkScreenSize()
		window.addEventListener('resize', checkScreenSize)

		return () => {
			window.removeEventListener('resize', checkScreenSize)
		}
	}, [])

	const captureOptions =
		trail.captures.length > 1
			? trail.captures.map((c) => ({ name: c.slug, path: c.slug }))
			: undefined

	const positions = useMemo(() => {
		return (
			captureData?.coordinates.map((coordinate: any, index: number) => ({
				href: coordinate[5] as string,
				latLng: [coordinate[1], coordinate[0]] as [number, number],
				index,
			})) ?? []
		)
	}, [captureData])

	const startMarker: MarkerData | null = useMemo(() => {
		if (!captureData?.coordinates?.[0]) return null
		return {
			id: `${trail.id}-start`,
			title: 'Start',
			alt: 'Start marker',
			position: [captureData.coordinates[0][1], captureData.coordinates[0][0]],
			popup: `Start @ ${captureData.coordinates[0][1]}, ${captureData.coordinates[0][0]}`,
			type: 'start',
		}
	}, [captureData, trail.id])

	const endMarker: MarkerData | null = useMemo(() => {
		const last = captureData?.coordinates?.[captureData.coordinates.length - 1]
		if (!last) return null
		return {
			id: `${trail.id}-end`,
			title: 'End',
			alt: 'End marker',
			position: [last[1], last[0]],
			popup: `End @ ${last[1]}, ${last[0]}`,
			type: 'end',
		}
	}, [captureData, trail.id])

	const mapMarkers: MarkerData[] = useMemo(() => {
		const currentFrameId = params['frame-id']
		const current = captureData?.coordinates?.find((c: any) =>
			c[5].endsWith(`/${currentFrameId}`),
		)
		const currentMarker: MarkerData | null = current
			? {
					id: currentFrameId!,
					title: 'Current location',
					alt: 'Current location marker',
					position: [current[1], current[0]],
					popup: `${currentFrameId} @ ${current[1]}, ${current[0]}`,
					type: 'current',
				}
			: null

		return [startMarker, endMarker, currentMarker].filter(
			Boolean,
		) as MarkerData[]
	}, [captureData, params, startMarker, endMarker])

	const onNavigate = useCallback(
		(href: string) => {
			setActiveTab('image')
			void navigate(href)
		},
		[navigate],
	)

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-hidden p-1">
			<div className="flex-none">
				<Breadcrumbs
					className="mb-2 sm:mb-4 md:mb-6"
					country={trail.country!}
					region={trail.region!}
					type="trail"
					trail={trail.slug}
					capture={trailCapture.slug}
					options={{ capture: captureOptions }}
				/>
			</div>

			{isDesktop ? (
				<TrailCaptureDesktopLayout
					trail={trail}
					trailCapture={trailCapture}
					captureData={captureData}
					currentFrameId={currentFrameId}
					updatedAtDisplay={updatedAtDisplay}
					captureOptions={captureOptions}
					positions={positions}
					mapMarkers={mapMarkers}
					blip={blip}
					onNavigate={onNavigate}
					onPositionChange={setBlip}
				>
					<Outlet />
				</TrailCaptureDesktopLayout>
			) : (
				<TrailCaptureMobileLayout
					trail={trail}
					trailCapture={trailCapture}
					captureData={captureData}
					currentFrameId={currentFrameId}
					updatedAtDisplay={updatedAtDisplay}
					captureOptions={captureOptions}
					positions={positions}
					mapMarkers={mapMarkers}
					blip={blip}
					onNavigate={onNavigate}
					onPositionChange={setBlip}
					activeTab={activeTab}
					onTabChange={setActiveTab}
				>
					<Outlet />
				</TrailCaptureMobileLayout>
			)}
		</div>
	)
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: () => (
					<div className="text-center">
						<p>Oops! Not found</p>
						<p className="mt-2 text-base">
							We're not able to locate the data for this trail or capture.
						</p>
					</div>
				),
			}}
		/>
	)
}
